'use client';

import { Divider } from '@common/components';
import { useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';

import type { FilterOption } from '../../constants';
import { FilterCheckboxList } from '../FilterCheckboxList';
import { FilterChipGroup } from '../FilterChipGroup';
import { FilterDrawerShell } from '../FilterDrawerShell';
import { FilterSection } from '../FilterSection';
import { FilterSidebarShell } from '../FilterSidebarShell';

interface GiftFilterProps {
  isDrawerOpen: boolean;
  onCloseDrawer: () => void;
  onApplyDrawer: () => void;
  categoryIds: number[];
  institutionIds: number[];
  onToggleCategory: (id: number) => void;
  onToggleInstitution: (id: number) => void;
  onReset: () => void;
}

export function GiftFilter({
  isDrawerOpen,
  onCloseDrawer,
  onApplyDrawer,
  categoryIds,
  institutionIds,
  onToggleCategory,
  onToggleInstitution,
  onReset,
}: GiftFilterProps) {
  const { data: categoryOptions = [], isLoading: isCategoryLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-category/primary'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }): FilterOption<number>[] =>
      (context?.contents ?? [])
        .filter((item) => Boolean(item.categoryName))
        .map((item) => ({
          value: item.giftCategoryId,
          label: item.categoryName,
        })),
  });

  const { data: institutionOptions = [], isLoading: isInstitutionLoading } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home/institution/for-search-filter'
    ]({
      query: { institutionFilterType: 'PRODUCT' },
    }),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ context }): FilterOption<number>[] =>
      (context?.contents ?? [])
        .filter((item) => Boolean(item.institutionName))
        .map((item) => ({
          value: item.institutionId,
          label: item.institutionName,
        })),
  });

  return (
    <>
      <FilterSidebarShell onReset={onReset}>
        <FilterSection title="카테고리">
          <FilterCheckboxList
            options={categoryOptions}
            selectedValues={categoryIds}
            onToggle={onToggleCategory}
            isLoading={isCategoryLoading}
            skeletonCount={6}
          />
        </FilterSection>

        <div className="py-8">
          <Divider />
        </div>

        <FilterSection title="지자체">
          <FilterCheckboxList
            options={institutionOptions}
            selectedValues={institutionIds}
            onToggle={onToggleInstitution}
            isLoading={isInstitutionLoading}
            skeletonCount={11}
          />
        </FilterSection>
      </FilterSidebarShell>

      <FilterDrawerShell
        isOpen={isDrawerOpen}
        onReset={onReset}
        onApply={onApplyDrawer}
        onClose={onCloseDrawer}
      >
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">카테고리</h3>
          <FilterChipGroup
            options={categoryOptions}
            selectedValues={categoryIds}
            onToggle={onToggleCategory}
            isLoading={isCategoryLoading}
            skeletonCount={6}
          />
        </div>
        <div className="flex flex-col gap-12">
          <h3 className="font-700 text-Gray-900 typo-body4">지자체</h3>
          <FilterChipGroup
            options={institutionOptions}
            selectedValues={institutionIds}
            onToggle={onToggleInstitution}
            isLoading={isInstitutionLoading}
            skeletonCount={11}
          />
        </div>
      </FilterDrawerShell>
    </>
  );
}
